import call from "./call";
import {
  CheckProgress,
  CompanyInfo,
  CompanyImportResult,
  CompanyResponse,
  CompanyStatusInterface,
  WebsiteResponse,
  StorageQuota,
} from "../Models/company";

export const fetchCompanies = async (
  page: number = 1,
  page_size: number = 10,
  sort_order: string = "name",
  sort_direction: string = "asc",
  search: string = "",
  workspace_shared_only?: boolean,
): Promise<CompanyResponse> => {
  const params = {
    page,
    page_size,
    sort_order,
    sort_direction,
    search,
    ...(workspace_shared_only && { workspace_shared_only: true }),
  };
  const { data } = await call.get<CompanyResponse>("/v1/company/", {
    params,
  });
  return data;
};

export const getCompaniesAll = async (
  page: number = 1,
  page_size: number = 10,
  sort_order: string = "name",
  search: string = "",
  user_id?: number,
): Promise<CompanyResponse> => {
  const { data } = await call.get<CompanyResponse>("/v1/company/all/", {
    params: { page, page_size, sort_order, search, user_id },
    snack: {
      error: "company.fetchAllError",
    },
  });
  return data;
};

export const getSingleCompany = async (id: string): Promise<CompanyInfo> => {
  const { data } = await call.get<CompanyInfo>(`/v1/company/${id}/`, {
    snack: {
      error: "company.fetchEntityError",
    },
  });
  return data;
};

export const checkProgress = async (evaluationId?: string) => {
  const { data } = await call.get<CheckProgress>(
    `/v1/company/check_progress/${evaluationId ? `${evaluationId}/` : ""}`,
  );
  return data;
};

export const getEvaluationStatus = async (id: string) => {
  const { data } = await call.get<CompanyStatusInterface>(
    `/v1/evaluation/status/${id}/`,
  );
  return data;
};

export const deleteSingleCompany = async (id: string) => {
  const response = await call.delete(`/v1/company/${id}/`, {
    snack: {
      success: "company.deleteSuccess",
      401: "company.workspaceShareUnauthorized",
      423: "company.entityProcessingFilesCannotBeDeleted",
      error: "company.deleteError",
    },
  });
  return response;
};

export const updateSingleCompany = async (
  id: string,
  name: string = "",
  about: string = "",
  location: string = "",
  industry: string = "",
  icon_id: number,
) => {
  const response = await call.put(
    `/v1/company/${id}/`,
    {
      name: name,
      about: about,
      location: location,
      industry: industry,
      icon_id: icon_id,
    },
    {
      snack: {
        success: "company.updateSuccess",
        error: "company.updateError",
      },
    },
  );
  return response;
};

export const updateAboutCompany = async (
  id: string,
  data: { evaluation_id: string; answers: unknown },
) => {
  const response = await call.put(
    `/v2/company/${id}/answers/`,
    {
      evaluation_id: data.evaluation_id,
      answers: data.answers,
    },
    {
      snack: {
        success: "company.updateAboutSuccess",
        423: "company.entityUnderEvaluation",
        error: "company.updateAboutError",
      },
    },
  );
  return response;
};

export const createCompany = async (
  name: string,
  about: string,
  industry: string,
  location: string,
  icon_id: number,
) => {
  const response = await call.post(
    "/v1/company/",
    {
      name: name,
      about: about,
      location: location,
      industry: industry,
      icon_id: icon_id,
    },
    {
      snack: {
        success: "company.createSuccess",
        418: "company.maxCompaniesReached",
        error: "company.createError",
      },
    },
  );
  return response;
};

export const getCompanyWebsites = async (
  companyId: string,
  page: number = 1,
  page_size: number = 10,
  sort_order: string = "name",
  sort_direction: string = "asc",
  search: string = "",
) => {
  const response = await call.get<WebsiteResponse>(
    `/v1/website/company/${companyId}/`,
    {
      params: { page, page_size, sort_order, sort_direction, search },
      snack: {
        error: "company.fetchWebsitesError",
      },
    },
  );
  return response.data;
};

export const uploadWebsite = async (
  companyId: string,
  name: string = " ",
  web_adress: string,
) => {
  const response = await call.post("/v1/website/create/", {
    company_id: companyId,
    name: name,
    web_adress: web_adress,
  });
  return response;
};

export const uploadFile = async (formData: FormData) => {
  const response = await call.post("/v1/document/", formData, {
    snack: {
      423: "company.evaluationInProgress",
    },
  });
  return response;
};

export const exportCompany = async (companyId: string) => {
  const response = await call.get(`/v1/company/${companyId}/export/`, {
    responseType: "blob",
    snack: {
      error: "company.exportError",
    },
  });
  return response;
};

export const importCompany = async (
  file: File,
): Promise<CompanyImportResult> => {
  const formData = new FormData();
  formData.append("file", file);

  const { data } = await call.post<CompanyImportResult>(
    "/v1/company/import/",
    formData,
    {
      snack: {
        success: "company.importSuccess",
        error: "company.importError",
      },
    },
  );
  return data;
};

export const getDownloadableFile = async (documentId: number) => {
  const response = await call.get(`/v1/document/${documentId}/`, {
    responseType: "blob",
  });
  return response;
};

export const getScrappedWebsiteContent = async (websiteId: number) => {
  const response = await call.get(`/v1/website/${websiteId}/scraped/`, {
    responseType: "blob",
  });
  return response;
};

export const deleteDocument = async (id: number) => {
  const response = await call.delete(`/v1/document/${id}/`, {
    snack: {
      423: "company.evaluationInProgressFileError",
      error: "company.documentDeleteFailed",
      success: "company.documentDeletedSuccess",
    },
  });
  return response;
};

export const stopDocument = async (id: number) => {
  const response = await call.post(
    `/v1/document/stop/${id}/`,
    {},
    {
      snack: {
        success: "company.documentStoppedSuccess",
        error: "company.documentStopFailed",
      },
    },
  );
  return response;
};

export const deleteWebsite = async (id: number) => {
  const response = await call.delete(`/v1/website/${id}/`, {
    snack: {
      success: "company.websiteDeletedSuccess",
      error: "company.websiteDeleteFailed",
    },
  });
  return response;
};

export const stopWebsite = async (id: number) => {
  const response = await call.post(
    `/v1/website/stop/${id}/`,
    {},
    {
      snack: {
        success: "company.websiteStoppedSuccess",
        error: "company.websiteStopFailed",
      },
    },
  );
  return response;
};

export const rescrapeWebsite = async (id: number) => {
  const response = await call.put(`/v1/website/rescrape/${id}/`, {});
  return response;
};

export const getStorageQuota = async (
  companyId: string,
): Promise<StorageQuota> => {
  const { data } = await call.get<StorageQuota>(
    `/v1/company/storage-quota/${companyId}/`,
    {
      snack: {
        error: "company.fetchStorageQuotaError",
      },
    },
  );
  return data;
};

export const workspaceShareCompany = async (companyId: string) => {
  const response = await call.put(
    `/v1/company/workspace-share/${companyId}/`,
    {},
    {
      snack: {
        success: "company.workspaceShareSuccess",
        error: "company.workspaceShareError",
      },
    },
  );

  return response;
};

export const detachDocumentFromQuestion = async (documentId: number) => {
  const response = await call.put(
    `/v1/document/detach-from-question/${documentId}/`,
    {},
    {
      snack: {
        success: "company.documentDetachedSuccess",
        error: "company.documentDetachFailed",
      },
    },
  );
  return response;
};
